// import { useEffect, useState } from "react";

// interface Admin {
//   id: number;
//   name: string;
//   email: string;
//   mobile: string;
//   group_id: number;
//   status: string;
//   image: string;
//   created_at: string;
// }

// export default function useAdminAuth() {
//   const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
//   const [admin, setAdmin] = useState<Admin | null>(null);
//   useEffect(() => {
//     fetch(`${BASE}/admin/users/me`, {
//       credentials: "include",
//     })
//       .then(res => res.json())
//       .then(res => {
//         if (res.data) {
//           setAdmin(res.data);
//         }
//       })
//       .catch(() => setAdmin(null));
//     }, []);


//   return { admin };
// }

import { useEffect, useMemo, useState } from "react";

export interface Admin {
  id: number;
  name: string;
  email: string;
  mobile: string;
  group_id: number;
  status: string;
  image: string;
  created_at: string;
}

export interface AdminRight {
  module: string;
  can_view: number;
  can_create: number;
  can_edit: number;
  can_delete: number;
}

type PermissionType = "view" | "create" | "edit" | "delete";

const getStoredAdminUser = (): Admin | null => {
  const possibleKeys = ["admin_user", "adminUser", "lyrazone_admin_user"];

  for (const key of possibleKeys) {
    const raw = localStorage.getItem(key);

    if (!raw) continue;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  return null;
};

const normalizeRight = (right: any): AdminRight => {
  return {
    module: String(
      right?.module || right?.module_name || right?.moduleKey || "",
    ).trim(),
    can_view: Number(right?.can_view ?? right?.view ?? right?.canView ?? 0),
    can_create: Number(
      right?.can_create ?? right?.create ?? right?.canCreate ?? 0,
    ),
    can_edit: Number(right?.can_edit ?? right?.edit ?? right?.canEdit ?? 0),
    can_delete: Number(
      right?.can_delete ?? right?.delete ?? right?.canDelete ?? 0,
    ),
  };
};

const getRightValue = (right: AdminRight, permission: PermissionType) => {
  if (permission === "view") return Number(right.can_view) === 1;
  if (permission === "create") return Number(right.can_create) === 1;
  if (permission === "edit") return Number(right.can_edit) === 1;
  if (permission === "delete") return Number(right.can_delete) === 1;

  return false;
};

export default function useAdminAuth() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const [admin, setAdmin] = useState<Admin | null>(null);
  const [rights, setRights] = useState<AdminRight[]>([]);
  const [loading, setLoading] = useState(true);
  const [rightsLoading, setRightsLoading] = useState(true);

  /*
    legacyMode protects the live app.

    If rights API is not available, or no rights are configured for an old group,
    old users will not suddenly lose access.

    Once rights are found for a group, permissions will be applied strictly.
  */
  const [legacyMode, setLegacyMode] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchJson = async (url: string) => {
      const response = await fetch(url, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      return response.json();
    };

    const fetchAdminAndRights = async () => {
      try {
        setLoading(true);
        setRightsLoading(true);

        let currentAdmin: Admin | null = null;

        try {
          const meRes = await fetchJson(`${BASE}/admin/users/me`);
          currentAdmin = meRes?.data || meRes?.admin || null;
        } catch (error) {
          console.warn("Admin /me API failed, using localStorage fallback.");
          currentAdmin = getStoredAdminUser();
        }

        if (!isMounted) return;

        setAdmin(currentAdmin);

        if (!currentAdmin?.group_id) {
          setRights([]);
          setLegacyMode(true);
          return;
        }

        try {
          const rightsRes = await fetchJson(
            `${BASE}/admin/users/rights/group/${currentAdmin.group_id}`,
          );

          const rawRights = rightsRes?.data || rightsRes?.rights || [];
          const normalizedRights = Array.isArray(rawRights)
            ? rawRights
                .map(normalizeRight)
                .filter((right) => right.module !== "")
            : [];

          if (!isMounted) return;

          setRights(normalizedRights);

          /*
            If no rights are configured yet, keep old behavior.
            This avoids breaking existing live clients/admins.
          */
          setLegacyMode(normalizedRights.length === 0);
        } catch (error) {
          console.warn(
            "Admin rights API failed. Keeping legacy full-access behavior.",
          );

          if (!isMounted) return;

          setRights([]);
          setLegacyMode(true);
        }
      } catch (error) {
        console.error("Admin auth check failed:", error);

        if (!isMounted) return;

        setAdmin(null);
        setRights([]);
        setLegacyMode(false);
      } finally {
        if (!isMounted) return;

        setLoading(false);
        setRightsLoading(false);
      }
    };

    fetchAdminAndRights();

    return () => {
      isMounted = false;
    };
  }, [BASE]);

  const rightsMap = useMemo(() => {
    const map = new Map<string, AdminRight>();

    rights.forEach((right) => {
      map.set(right.module, right);
    });

    return map;
  }, [rights]);

  const hasPermission = (module: string, permission: PermissionType) => {
    /*
      Important for live app:
      if legacyMode is true, old behavior continues.
    */
    if (legacyMode) return true;

    const right = rightsMap.get(module);

    if (!right) return false;

    return getRightValue(right, permission);
  };

  const canView = (module: string) => hasPermission(module, "view");
  const canCreate = (module: string) => hasPermission(module, "create");
  const canEdit = (module: string) => hasPermission(module, "edit");
  const canDelete = (module: string) => hasPermission(module, "delete");

  const canViewAny = (modules: string[]) => {
    if (legacyMode) return true;

    return modules.some((module) => canView(module));
  };

  return {
    admin,

    rights,
    rightsMap,
    loading,
    rightsLoading,
    legacyMode,

    canView,
    canCreate,
    canEdit,
    canDelete,
    canViewAny,
    hasPermission,
  };
}